import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'
import { PushNotifications } from '@capacitor/push-notifications'
import { FCM } from '@capacitor-community/fcm'
import * as Sentry from '@sentry/react'
import { useAuth } from './useAuth'
import { useRegisterFCMDevice } from './useFCMDevices'
import type { FCMDevicePlatform } from '../types/notification.types'

export const FCM_TOKEN_STORAGE_KEY = 'fcm:token'

const ANDROID_GENERAL_CHANNEL = {
  id: 'general',
  name: 'General',
  importance: 4,
  visibility: 1,
} as const

let registrationInFlight = false

const setupApnsWaiter = async (): Promise<{ arrived: Promise<void> }> => {
  let resolver!: () => void
  let rejecter!: (e: unknown) => void
  const arrived = new Promise<void>((res, rej) => {
    resolver = res
    rejecter = rej
  })

  let regHandle: { remove: () => void } | null = null
  let errHandle: { remove: () => void } | null = null
  const cleanup = () => {
    regHandle?.remove()
    errHandle?.remove()
  }

  ;[regHandle, errHandle] = await Promise.all([
    PushNotifications.addListener('registration', (token) => {
      console.log('[fcm] APNs registration arrived', token?.value?.slice(0, 12))
      cleanup()
      resolver()
    }),
    PushNotifications.addListener('registrationError', (err) => {
      console.error('[fcm] APNs registrationError', err)
      cleanup()
      rejecter(err)
    }),
  ])

  return { arrived }
}

export const useFCMRegistration = () => {
  const { isAuthenticated } = useAuth()
  const { mutateAsync: registerDevice } = useRegisterFCMDevice()

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !isAuthenticated) return
    if (registrationInFlight) return

    let cancelled = false
    registrationInFlight = true

    const run = async () => {
      try {
        console.log('[fcm] registration hook starting')
        if (Capacitor.getPlatform() === 'android') {
          await PushNotifications.createChannel(ANDROID_GENERAL_CHANNEL).catch(() => undefined)
        }

        let perm = await PushNotifications.checkPermissions()
        console.log('[fcm] checkPermissions', perm.receive)
        if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
          perm = await PushNotifications.requestPermissions()
          console.log('[fcm] requestPermissions', perm.receive)
        }
        if (perm.receive !== 'granted' || cancelled) return

        const { arrived: apnsArrived } = await setupApnsWaiter()
        console.log('[fcm] calling PushNotifications.register()')
        await PushNotifications.register()
        console.log('[fcm] waiting for APNs registration event')
        await apnsArrived
        if (cancelled) return

        console.log('[fcm] calling FCM.getToken()')
        const { token } = await FCM.getToken()
        console.log('[fcm] FCM token received', token?.slice(0, 16))
        if (!token || cancelled) return

        const lastToken = localStorage.getItem(FCM_TOKEN_STORAGE_KEY)
        if (lastToken === token) {
          console.log('[fcm] token unchanged, skipping POST')
          return
        }

        const platform = Capacitor.getPlatform() as FCMDevicePlatform
        const [deviceId, deviceInfo, appInfo] = await Promise.all([
          Device.getId().catch(() => null),
          Device.getInfo().catch(() => null),
          App.getInfo().catch(() => null),
        ])
        if (cancelled) return

        console.log('[fcm] POSTing /notifications/devices/')
        await registerDevice({
          token,
          platform,
          device_id: deviceId?.identifier,
          app_version: appInfo?.version,
          os_version: deviceInfo?.osVersion,
        })
        localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token)
        console.log('[fcm] registration complete')
      } catch (err) {
        console.error('[fcm] registration failed', err)
        Sentry.captureException(err, { tags: { feature: 'fcm-registration' } })
      } finally {
        registrationInFlight = false
      }
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, registerDevice])
}
